import { useEffect, useMemo } from 'react';
import { useAssetUrlStore, ensureAssetsLoaded, assetUrl } from './assetUrls';
import { rewriteAssetRefs, expensiveRefs } from './resolveAssets';
import { useSignedStore, ensureSigned, signedUrl } from './sign';

const REF_RE = /asset:\/\/(sha256-[a-f0-9]{32})/g;

/**
 * Rewrite `asset://` refs in `html` to objectURLs (and signed CDN urls for
 * expensive ai=/bits=16 refs). Loads any missing blobs + mints any missing
 * signatures async and re-renders when either cache changes, so a freshly
 * loaded blob / minted signature resolves on the next paint. No asset refs →
 * cheap no-op.
 */
export function useAssetResolvedHtml(html: string): string {
  // Subscribe to both caches so this component re-renders when either fills.
  const urls = useAssetUrlStore((s) => s.urls);
  const sigs = useSignedStore((s) => s.sigs);

  const ids = useMemo(() => {
    const set = new Set<string>();
    for (const m of html.matchAll(REF_RE)) set.add(m[1]);
    return [...set];
  }, [html]);

  const expensive = useMemo(() => expensiveRefs(html), [html]);

  useEffect(() => {
    if (ids.length) void ensureAssetsLoaded(ids);
  }, [ids]);

  useEffect(() => {
    if (expensive.length) void ensureSigned(expensive);
  }, [expensive]);

  return useMemo(
    () => (ids.length ? rewriteAssetRefs(html, assetUrl, signedUrl) : html),
    // `urls` + `sigs` in deps so re-resolution happens when either cache fills.
    [html, ids, urls, sigs],
  );
}
